import i18n_base_ui from '@/locales/keys/i18n_base_ui.json';
import { REMOTE__images_file_data_upload_to_workspace } from '@/state/images/upload/REMOTE__images_file_data_upload_to_workspace';
import { AppDispatch, store } from '@/state/store';
import { CALLBACK__file_data, CALLBACK__file_data_list, CALLBACK__toast, CALLBACK__translation } from '@/types/types';
import { assert_array_has_1_item, assert_instanceof, assert_number_gt_0, assert_type_array } from '@shared/asserts/utils_asserts';
import { CONSTANTS__shared } from '@shared/constants/CONSTANTS__shared';
import { DTO_REQUEST__images_upload } from '@shared/dto/+image/DTO_REQUEST__images_upload';
import { DTO__file_data } from '@shared/dto/+image/image_asset_base/DTO__file_data';
import { DTO__image_asset } from '@shared/dto/+image/image_asset_base/DTO__image_asset';
import { ENUM__image_asset_category } from '@shared/dto/generation/graph_invoke/_fields/ENUM__image_asset_category';
import { DTO__grid2d } from '@shared/dto/grid2d/DTO__grid2d';
import { DTO_RESPONSE__error } from '@shared/network/DTO_RESPONSE__error';
import { format_size } from '@shared/utils/utils_files';
import { convert_file_to_webp, try_downscale_image_file } from './UTILS__image';
import { file_to_blob } from './utils_files_client';
import { string_to_span } from './utils_html';

export class UTILS__upload {
  static async image_to_workspace(file_data_list: DTO__file_data[], category: ENUM__image_asset_category, workspace_id: string) {
    let request_image_upload = new DTO_REQUEST__images_upload();
    request_image_upload.category = category;

    request_image_upload.file_data_list = file_data_list;
    request_image_upload.workspace_id = workspace_id;

    let action_result = await store.dispatch(REMOTE__images_file_data_upload_to_workspace(request_image_upload));

    if (action_result.payload instanceof DTO_RESPONSE__error) return DTO__image_asset.CREATE_STUB();

    assert_type_array(action_result.payload);
    let image = assert_array_has_1_item(action_result.payload);

    assert_instanceof(image, DTO__image_asset);

    return image;
  }
}

export async function FN_FILE_LIST__to_file_data_list(toast: CALLBACK__toast, file_list: FileList, t: CALLBACK__translation, files_limit: number) {
  let out: DTO__file_data[] = [];

  for (let file of file_list) {
    if (!file) continue;

    let file_data = await create_file_data(file, toast, t);

    if (!file_data.is_stub()) out.push(file_data);

    if (isNaN(files_limit)) continue;

    assert_number_gt_0(files_limit);

    if (out.length >= files_limit) break; //need to stop because we reach limit
  }

  return out;
}

export function upload_image_single(toast: CALLBACK__toast, callback: CALLBACK__file_data, t: CALLBACK__translation) {
  const input = document.createElement('input');

  input.type = 'file';
  input.accept = CONSTANTS__shared.UPLOAD_FILE_ALLOWED_TYPES.join(',');
  input.multiple = false;

  input.onchange = async e => {
    const file = (e.target as HTMLInputElement).files?.[0];
    if (!file) return;

    let file_data = await create_file_data(file, toast, t);

    if (!file_data.is_stub()) callback(file_data);
  };

  input.click();
  input.remove();
}

export function upload_images_multiple(toast: CALLBACK__toast, callback: CALLBACK__file_data_list, t: CALLBACK__translation, files_limit = NaN) {
  const input = document.createElement('input');

  input.type = 'file';
  input.accept = CONSTANTS__shared.UPLOAD_FILE_ALLOWED_TYPES.join(',');
  input.multiple = true;

  input.onchange = async e => {
    let files = (e.target as HTMLInputElement).files || [];

    let out = await FN_FILE_LIST__to_file_data_list(toast, files as FileList, t, files_limit);

    if (out.length > 0) callback(out);
  };

  input.click();
  input.remove();
}

export function upload_many_images_to_user_task_references(
  toast: CALLBACK__toast,
  dispatch: AppDispatch,
  grid2d: DTO__grid2d,
  user_task_id: string,
  t_base_ui: CALLBACK__translation,
) {
  async function callback(file_data_list: DTO__file_data[]) {
    if (file_data_list.is_empty()) return;

    await send_images_as_user_task_references(file_data_list, dispatch, grid2d, user_task_id);
  }

  upload_images_multiple(toast, callback, t_base_ui);
}

export async function create_file_data(file: File, toast: CALLBACK__toast, t_base_ui: CALLBACK__translation): Promise<DTO__file_data> {
  let { UPLOAD_FILE_ALLOWED_TYPES, UPLOAD_FILE_SIZE_MAX } = CONSTANTS__shared;

  if (!UPLOAD_FILE_ALLOWED_TYPES.includes(file.type)) {
    toast({
      title: t_base_ui(i18n_base_ui.toasts.create_file_data_not_allowed_type.title),
      description: string_to_span(
        t_base_ui(i18n_base_ui.toasts.create_file_data_not_allowed_type.description, { types: UPLOAD_FILE_ALLOWED_TYPES.join('\n• ') }),
      ),
    });

    return DTO__file_data.CREATE_STUB();
  }

  file = await try_downscale_image_file(file);
  file = await convert_file_to_webp(file);

  if (file.size > UPLOAD_FILE_SIZE_MAX) {
    toast({
      title: t_base_ui(i18n_base_ui.toasts.create_file_data_too_big.title),
      description: t_base_ui(i18n_base_ui.toasts.create_file_data_too_big.description, { size: format_size(UPLOAD_FILE_SIZE_MAX) }),
    });

    return DTO__file_data.CREATE_STUB();
  }

  let blob = await file_to_blob(file);

  let out = new DTO__file_data();
  out.blob = blob;
  out.filename = file.name;
  out.mimetype = file.type;
  out.size = file.size;

  return out;
}
